import { dehydrate, QueryClient } from "@tanstack/react-query";
import { GetStaticPropsContext } from "next/types";
import { BreadcrumbJsonLd } from "next-seo";
import ArticleSeo from "@/components/article/ArticleSeo";
import ArticleContainer from "@/components/container/ArticleContainer";
import ArticlePreviewContainer from "@/components/container/ArticlePreviewContainer";
import SportContainer from "@/components/container/SportContainer";
import PreviewSuspense from "@/components/preview/PreviewSuspense";
import SportSeo from "@/components/sport/SportSeo";
import {
  Article,
  GenericSport,
  StatusSelector,
  GetRedirectQuery,
  GetSupportedSportsQuery,
  GetArticleBySlugQuery,
  Promotion,
  GetPromotionsQuery,
  GetSingleSportBetsQueryQuery,
} from "@/graphql/gql/graphql";
import { BreadcrumbSegment } from "@/types/breadcrumbs";
import { ArticleList } from "@/types/common";
import {
  getAllArticles,
  getArticleBySlug,
  getAvailableSports,
  getFeaturedArticlesBySport,
  getPromotions,
  getRedirect,
  getSingleSportBets,
} from "@/utils/api";
import { useBreadcrumbs } from "@/utils/hooks/useBreadcrumbs";
import logger from "@/utils/logger";
import { loadHeader, loadRecentArticles, loadSports } from "@/utils/ssr";
import { filterEmpty, handlePromise, findSportIfSportSlug } from "../utils";

export type SingleSportBets = NonNullable<
  GetSingleSportBetsQueryQuery["getSupportedSports"]
>[number];
interface DynamicProps {
  article?: Article;
  sport?: GenericSport;
  featuredArticles?: Article[];
  preview?: boolean;
  sportName?: string;
  promotions?: Promotion[];
  featuredBets?: SingleSportBets;
}

const DynamicPage = ({
  article,
  sport,
  featuredArticles,
  preview,
  sportName,
  promotions,
  featuredBets,
}: DynamicProps) => {
  const breadcrumbSegments: BreadcrumbSegment[] = [];
  if (sport) {
    breadcrumbSegments.push({
      name: sport.name.toUpperCase(),
      path: `/${sport.slug}`,
    });
  }
  const { trail, itemListElements } = useBreadcrumbs(breadcrumbSegments);

  if (featuredArticles && sport) {
    return (
      <>
        <SportSeo sport={sport} />
        <BreadcrumbJsonLd itemListElements={itemListElements} />
        <SportContainer
          featuredBets={featuredBets}
          featuredArticles={featuredArticles}
          sport={sport}
          trail={trail}
        />
      </>
    );
  }

  if (article && sportName) {
    return preview ? (
      <PreviewSuspense>
        <ArticlePreviewContainer article={article} />
      </PreviewSuspense>
    ) : (
      <>
        <ArticleSeo article={article} />
        <ArticleContainer article={article} promotions={promotions} />
      </>
    );
  }
  return <div />;
};

export async function getServerSideProps({
  params,
  preview = false,
}: GetStaticPropsContext) {
  const isPreview = !!preview;
  const queryClient = new QueryClient();
  await Promise.all([loadHeader(queryClient), loadSports(queryClient)]).catch(
    logger?.error
  );
  if (!params?.slug) {
    return { notFound: true };
  }

  // Redirect to lowercase destination
  const pageSlug = params?.slug?.toString() ?? "";
  const pageSlugLowerCase = pageSlug.toLocaleLowerCase();
  if (pageSlug !== pageSlugLowerCase) {
    return {
      redirect: {
        destination: `/${pageSlugLowerCase}`,
        permanent: true,
      },
    };
  }

  // Check if slug is a selected sport slug or an article slug
  const [sportsSettled, articleSettled, redirectSettled] =
    await Promise.allSettled([
      getAvailableSports({ isPreview }),
      getArticleBySlug({
        slug: params?.slug?.toString(),
        contentStatus: isPreview
          ? StatusSelector.Both
          : StatusSelector.Published,
        isPreview,
      }),
      getRedirect(params?.slug?.toString()),
    ]);
  const supportedSportsResponse = handlePromise({
    settled: sportsSettled,
    defaultValue: {} as GetSupportedSportsQuery,
    errorMessage: "Slug page getAvailableSports error",
  });
  const supportedSports = supportedSportsResponse.getSupportedSports;
  const selectedSport = findSportIfSportSlug(
    supportedSports,
    pageSlugLowerCase
  );

  // If slug refers to a sport, get featured articles
  if (selectedSport) {
    let featuredArticles: ArticleList = [];
    let featuredBets: SingleSportBets = {};
    try {
      try {
        const [[featuredArticlesResponse], featuredBetsResponse] =
          await Promise.all([
            getFeaturedArticlesBySport([selectedSport.slug]),
            getSingleSportBets({ slug: selectedSport.slug }),
          ]);

        featuredArticles = featuredArticlesResponse.getFeaturedArticles || [];

        if (featuredBetsResponse.getSupportedSports) {
          featuredBets = featuredBetsResponse.getSupportedSports[0];
        }
      } catch (err) {
        const allArticles = await getAllArticles({
          sportName: selectedSport.name,
        });
        featuredArticles = filterEmpty(
          allArticles.getArticles?.articles.map((article) => article?.entity) ||
            []
        ) as ArticleList;
        logger?.error(
          `Error fetching bets and articles for ${selectedSport.name}`,
          err
        );
      }
      const moreArticlesClient = await loadRecentArticles(queryClient, {
        limit: 12,
        sportName: selectedSport.name,
        hideDeprioritizedContent: true,
      });
      return {
        props: {
          dehydratedState: dehydrate(moreArticlesClient),
          sport: selectedSport,
          featuredArticles: filterEmpty(featuredArticles || []),
          featuredBets: featuredBets,
        },
      };
    } catch (err) {
      logger?.error("Error: [slug] is selected sport", err);
      return { notFound: true };
    }
  }

  // If slug refers to an article, get the article
  const articleResponse = handlePromise({
    settled: articleSettled,
    defaultValue: {} as GetArticleBySlugQuery,
    errorMessage: "Slug page getArticleBySlug error",
  });
  const redirectResponse = handlePromise({
    settled: redirectSettled,
    defaultValue: undefined as unknown as GetRedirectQuery,
  });

  if (
    redirectResponse?.getRedirect?.toSlug &&
    redirectResponse.getRedirect.toSlug !== pageSlug
  ) {
    return {
      redirect: {
        destination: `/${redirectResponse.getRedirect.toSlug}`,
        permanent: redirectResponse.getRedirect.isPermanent,
      },
    };
  }

  try {
    const article = articleResponse.getSingleArticle;
    if (!article) {
      logger.info("No article found", { slug: params?.slug?.toString() });
      return { notFound: true };
    }
    // If we are in preview mode, the article may not have required fields.
    // In case the article is WIP, ensure all fields at least have default values
    if (preview) {
      const defaultSport = { name: "" };
      const defaultDate = "";
      const defaultAuthor = {
        id: "",
        name: "",
        thumbnailUrl: null,
        socialMedia: null,
      };

      article.title = article.title || "";
      article.firstPublishedAt = article.firstPublishedAt || defaultDate;
      article.body = article.body || "[]";
      article.author = article.author || defaultAuthor;
      article.sport = article.sport || defaultSport;
      article.tags = article.tags || undefined;
    }
    let promotions = [] as GetPromotionsQuery["getPromotions"];
    try {
      const [promotionsData] = await Promise.all([
        getPromotions({ tags: article.tags }),
        loadRecentArticles(queryClient, { limit: 6 }),
      ]);
      promotions = promotionsData?.getPromotions || [];
    } catch (e) {
      logger?.error(e);
    }
    return {
      props: {
        dehydratedState: dehydrate(queryClient),
        article,
        ...params,
        sportName: article.sport.name,
        preview,
        promotions,
      },
    };
  } catch (err) {
    logger.error(`Error: [slug] is article. Preview is ${preview}`, err);
    return {
      notFound: true,
    };
  }
}

export default DynamicPage;
